import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Flex, Group, Skeleton, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCirclePlus } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import Router from 'next/router';
import Helper from '@/utils/helper';
import ToastContext from '@/utils/toast-context';
import { justCompletedSetupAtom } from '@/utils/atoms';
import { getUserFromCookie } from '@/utils/auth';
import CompanyService from '@/services/company-service';
import { OnboardingStatus } from '@/PageComponents/Onboarding/useOnboardingStatus';
import { formatDemoDate, formatDemoRelative, getDemoTiming, getManagerCardState, isDemoBooked, isIntegrationConnected, managerFromOwner, STEP_ORDER, StepId, toManager } from '@/PageComponents/Onboarding/onboarding-rules';
import { DEMO_MANAGER, HUBSPOT_DEMO_MEETING_URL, MAX_LOGO_BYTES, ONBOARDING_INVITE_ROLE, ONBOARDING_PARTNERS, ONBOARDING_ROUTES } from '@/PageComponents/Onboarding/onboarding-config';
import { MeetingBooking } from '@/PageComponents/Onboarding/hubspot-booking';
import OnboardingStepCard from '@/PageComponents/Onboarding/components/OnboardingStepCard';
import SetupProgress from '@/PageComponents/Onboarding/components/SetupProgress';
import ManagerCard from '@/PageComponents/Onboarding/components/ManagerCard';
import BeforeYouStartModal from '@/PageComponents/Onboarding/components/BeforeYouStartModal';
import BookDemoModal from '@/PageComponents/Onboarding/components/BookDemoModal';
import LogoSetup from '@/PageComponents/Onboarding/components/LogoSetup';
import ImportDataSetup from '@/PageComponents/Onboarding/components/ImportDataSetup';
import IntegrationInfoModal from '@/PageComponents/Onboarding/components/IntegrationInfoModal';
import { PrimaryButton } from '@/PageComponents/Onboarding/components/OnboardingButtons';
import InviteEmployeesModal from '@/components/modals/employee/invite-employees';
import analyticsService from '@/services/analytics/analytics-service';
import { BookDemoLocation, DemoState, ONBOARDING_EVENTS, packageSlug, setOnboardingAnalyticsContext, trackOnboarding, trackOnboardingOnce, useOnboardingModalAnalytics, useOnboardingPageView } from '@/PageComponents/Onboarding/onboarding-analytics';
import styles from '@/PageComponents/Onboarding/OnboardingDashboard.module.css';

const readFileAsBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).replace(/^data:.+;base64,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});

const go = (url: string) => Helper.nextRouter(Router.push, url);
// Keeps the user on the dashboard while they set the integration up.
const openInNewTab = (url: string) => window.open(url, '_blank', 'noopener');

export const OnboardingSkeleton: FC = () => (
    <Box p="lg" className={styles.page} data-testid="onboarding-skeleton">
        <Skeleton h={32} w={320} mb="lg" />
        <Flex gap="md" mb="xl" direction={{ base: 'column', sm: 'row' }}>
            <Skeleton h={130} flex={1} />
            <Skeleton h={130} w={{ base: '100%', sm: 220 }} />
        </Flex>
        <Stack gap="md">{STEP_ORDER.map(s => <Skeleton key={s} h={90} />)}</Stack>
    </Box>
);

// The step header buttons share a width so they line up down the right-hand side (the design has 165px,
// but "Invite an admin" needs 192px in our font). A prop, not CSS:
// the theme sets the button's min-width inline.
const HEADER_BUTTON_WIDTH = 192;

const onlyExpanded = (step: StepId | null): Record<StepId, boolean> =>
    ({ admin: step === 'admin', brand: step === 'brand', firstJob: step === 'firstJob' });

const OnboardingDashboard: FC<{ status: OnboardingStatus }> = ({ status }) => {
    const { steps } = status;
    const user = useMemo(() => getUserFromCookie(), []);
    const firstName = user?.firstName ?? '';
    const toast = useContext<any>(ToastContext);
    const [justCompletedSetup, setJustCompletedSetup] = useAtom(justCompletedSetupAtom);

    const [beforeYouStartOpen, setBeforeYouStartOpen] = useState(false);
    // What opened Before-you-start: the end of sign-up, or "What to expect".
    const [beforeYouStartTrigger, setBeforeYouStartTrigger] = useState<'sign_up' | 'what_to_expect'>('sign_up');
    const [bookDemoOpen, setBookDemoOpen] = useState(false);
    const [bookDemoLocation, setBookDemoLocation] = useState<BookDemoLocation>('manager_card');
    // Booking from Before-you-start only steps away from it: it comes back when the booking modal closes.
    const [returnToBeforeYouStart, setReturnToBeforeYouStart] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [integrationInfoOpen, setIntegrationInfoOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    // "Invite admins" (step 1 already done): there's nothing to skip, so the dialog offers Cancel.
    const [invitingMore, setInvitingMore] = useState(false);

    // One step selected at a time: selecting a step closes the others. Only step 2 opens (an accordion);
    // steps 1 and 3 keep their button in the header and are just marked active.
    const [expanded, setExpanded] = useState<Record<StepId, boolean>>(() => onlyExpanded(steps.firstIncomplete));
    // The last step the user clicked; it only shows as active (blue border) while it is selected.
    const [lastToggled, setLastToggled] = useState<StepId | null>(steps.firstIncomplete);
    const isActive = (step: StepId) => lastToggled === step && expanded[step];

    // Sign-up just finished: show the modal straight away.
    // The flag is cleared only after opening, so a remount can't swallow the trigger.
    useEffect(() => {
        if (justCompletedSetup) {
            setBeforeYouStartTrigger('sign_up');
            setBeforeYouStartOpen(true);
            setJustCompletedSetup(false);
        }
    }, [justCompletedSetup]);

    // When a step becomes complete, collapse it and move the user on to the next one.
    const previousComplete = useRef(steps.complete);
    useEffect(() => {
        const newlyComplete = STEP_ORDER.filter(s => steps.complete[s] && !previousComplete.current[s]);
        previousComplete.current = steps.complete;
        if (newlyComplete.length === 0) return;
        setExpanded(onlyExpanded(steps.firstIncomplete));
        setLastToggled(steps.firstIncomplete);
    }, [steps.complete.admin, steps.complete.brand, steps.complete.firstJob]);

    // Clicking the open step 2 closes it; clicking a step without a body only selects it.
    const toggle = (step: StepId) => {
        setExpanded(prev => onlyExpanded(step === 'brand' && prev.brand ? null : step));
        setLastToggled(step);
    };

    const timing = getDemoTiming(status.demoBooking);
    const demoBooked = isDemoBooked(timing);
    // Whoever runs the booked (or held) demo is presented as the account manager until
    // GET /AccountManager/Get names one.
    const demoOwner = (demoBooked || status.demo.held) && status.demo.owner ? toManager(status.demo.owner.name, status.demo.owner.email) : null;
    const accountManager = status.accountManager ?? demoOwner;
    const managerState = getManagerCardState(timing, status.demo.held, !!accountManager);
    const demoDate = timing.kind === 'upcoming' ? formatDemoDate(status.demoBooking?.startISO) : null;
    // Whoever HubSpot says runs the demo; the configured demo manager until a meeting exists.
    const demoManager = managerFromOwner(status.demo.owner, DEMO_MANAGER);

    // ---- Analytics -------------------------------------------------------------------------------------
    const demoState: DemoState = status.demo.held ? 'held' : demoBooked ? 'booked' : 'none';
    // Declared first, so the effects below (and any click) already see the current progress.
    useEffect(() => {
        setOnboardingAnalyticsContext({ page: 'dashboard', steps_completed: steps.completedCount, demo_state: demoState });
    }, [steps.completedCount, demoState]);

    // Recordings of sessions that saw onboarding can then be found in Clarity.
    useOnboardingPageView('dashboard', window.location.pathname === '/', { manager_card_state: managerState },
        () => analyticsService.tagSession('onboarding', 'dashboard'));

    // Milestones, the first time this browser sees them for the company (they may have happened
    // elsewhere, e.g. the first job on the create job page, or by another user). `detected_on_load`
    // tells those apart from steps finished on the dashboard: it's true when the step was already
    // complete as the dashboard loaded (including steps done before tracking started).
    const completeOnLoad = useRef(steps.complete);
    useEffect(() => {
        STEP_ORDER.filter(step => steps.complete[step]).forEach(step =>
            trackOnboardingOnce(`step:${step}`, ONBOARDING_EVENTS.stepCompleted, { step, detected_on_load: !!completeOnLoad.current[step] },
                { [`onboarding_step_${step}_completed_at`]: new Date().toISOString() }));
    }, [steps.complete.admin, steps.complete.brand, steps.complete.firstJob]);

    useEffect(() => {
        if (!isIntegrationConnected(status.integration)) return;
        const partner = ONBOARDING_PARTNERS.find(p => p.partner === status.integration?.Partner);
        trackOnboardingOnce('integration', ONBOARDING_EVENTS.integrationConnected, { package: partner ? packageSlug(partner.name) : String(status.integration?.Partner) });
    }, [status.integration?.Partner, status.integration?.Status]);

    useEffect(() => {
        if ((status.completedImportCount ?? 0) > 0) trackOnboardingOnce('imports', ONBOARDING_EVENTS.dataImported, { import_count: status.completedImportCount });
    }, [status.completedImportCount]);

    useEffect(() => {
        if (status.hasError) trackOnboarding(ONBOARDING_EVENTS.setupErrorShown);
    }, [status.hasError]);

    const beforeYouStartOutcome = useOnboardingModalAnalytics('before_you_start', beforeYouStartOpen, { trigger: beforeYouStartTrigger });
    const connectYourDataOutcome = useOnboardingModalAnalytics('connect_your_data', integrationInfoOpen, { trigger: 'find_out_more' });
    const inviteOutcome = useOnboardingModalAnalytics('invite_admins', inviteOpen, { trigger: invitingMore ? 'invite_admins' : 'invite_an_admin' });

    const openWhatToExpect = () => {
        setBeforeYouStartTrigger('what_to_expect');
        setBeforeYouStartOpen(true);
    };

    const openInvite = (more: boolean) => {
        setInvitingMore(more);
        setInviteOpen(true);
    };

    const openBookDemo = (location: BookDemoLocation) => {
        trackOnboarding(ONBOARDING_EVENTS.bookDemoClicked, { location });
        if (location === 'before_you_start') beforeYouStartOutcome('book_demo');
        if (location === 'connect_your_data') connectYourDataOutcome('book_demo');
        setBookDemoLocation(location);
        setReturnToBeforeYouStart(beforeYouStartOpen);
        setBeforeYouStartOpen(false);
        setIntegrationInfoOpen(false);
        setBookDemoOpen(true);
    };

    const closeBookDemo = () => {
        setBookDemoOpen(false);
        if (returnToBeforeYouStart) setBeforeYouStartOpen(true);
        setReturnToBeforeYouStart(false);
    };

    const onBooked = (booking: MeetingBooking) => {
        status.setLocalDemoBooking({ startISO: booking.startISO, bookedAtISO: new Date().toISOString() });
        showNotification({ color: 'green', message: 'Your demo is booked — we look forward to meeting you.' });
    };

    const onLogoSelected = async (file: File) => {
        if (!status.company?.Name) return;
        if (file.size > MAX_LOGO_BYTES) {
            showNotification({ color: 'yellow.7', message: 'Logo must be smaller than 3MB.' });
            trackOnboarding(ONBOARDING_EVENTS.logoUploadFailed, { reason: 'too_large' });
            return;
        }
        setUploadingLogo(true);
        try {
            const company = status.company;
            const response = await CompanyService.saveCompany({
                ...company,
                FileBase64: await readFileAsBase64(file),
                CurrencyID: company?.Currency?.ID ?? company?.CurrencyID ?? null,
            }, toast);
            if (response?.Name) {
                showNotification({ color: 'green', message: 'Logo uploaded successfully' });
                trackOnboarding(ONBOARDING_EVENTS.logoUploaded, { replaced: !!company?.LogoUrl });
                status.refetchCompany();
            } else {
                trackOnboarding(ONBOARDING_EVENTS.logoUploadFailed, { reason: 'server_error' });
            }
        } catch {
            showNotification({ color: 'red', message: "We couldn't upload your logo. Please try again." });
            trackOnboarding(ONBOARDING_EVENTS.logoUploadFailed, { reason: 'server_error' });
        } finally {
            setUploadingLogo(false);
        }
    };

    return (
        <Box p={{ base: 'md', sm: 'lg' }} className={styles.page} data-testid="onboarding-dashboard">
            <Title order={2} mb={20} className={styles.welcome}>{firstName ? `Welcome to ServCraft, ${firstName}.` : 'Welcome to ServCraft.'}</Title>

            {status.hasError && (
                <Alert color="yellow" mb="md" title="Some setup info couldn't load">
                    <Button size="xs" variant="light" color="yellow" onClick={status.retry}>Retry</Button>
                </Alert>
            )}

            {/* Progress comes first and stays put whatever state the manager card is in. */}
            <Flex gap={16} mb={32} direction={{ base: 'column', sm: 'row' }}>
                <Box w={{ base: '100%', sm: 224 }} style={{ flexShrink: 0 }}>
                    <SetupProgress completed={steps.completedCount} total={STEP_ORDER.length} />
                </Box>
                <Box flex={1} miw={0}>
                    <ManagerCard
                        state={managerState}
                        timing={timing}
                        demoHeld={status.demo.held}
                        demoManager={demoManager}
                        accountManager={accountManager}
                        demoDate={demoDate}
                        onBookDemo={() => openBookDemo('manager_card')}
                        onWhatToExpect={openWhatToExpect}
                    />
                </Box>
            </Flex>

            <Title order={3} mb={24} className={styles.sectionTitle}>
                {steps.allComplete ? "You've set up the basics, we recommend adding more jobs." : 'Set up the basics'}
            </Title>
            <Stack gap={16}>
                <OnboardingStepCard
                    index={1}
                    stepId="admin"
                    title="Bring in the person who will run ServCraft day to day."
                    description="Usually your office admin, yourself, or the person who will have the most time to test the system."
                    complete={steps.complete.admin}
                    // Stays incomplete until someone else is invited: this step can't be done alone.
                    headerAction={steps.complete.admin
                        ? <PrimaryButton id="onboarding-invite-admins" miw={HEADER_BUTTON_WIDTH} leftSection={<IconCirclePlus size={20} />} onClick={() => openInvite(true)}>Invite admins</PrimaryButton>
                        : <PrimaryButton id="onboarding-invite-an-admin" miw={HEADER_BUTTON_WIDTH} leftSection={<IconCirclePlus size={20} />} onClick={() => openInvite(false)}>Invite an admin</PrimaryButton>}
                    highlighted={isActive('admin')}
                    onToggle={() => toggle('admin')}
                />

                <OnboardingStepCard
                    index={2}
                    stepId="brand"
                    title="Make ServCraft yours"
                    // Open, the tasks speak for themselves.
                    description={expanded.brand ? undefined : steps.complete.brand ? 'Add your logo in ServCraft and import your data.' : 'Add your logo and import your data.'}
                    complete={steps.complete.brand}
                    expanded={expanded.brand}
                    highlighted={isActive('brand')}
                    onToggle={() => toggle('brand')}
                    indentBody={false}
                >
                    <Stack gap={12}>
                        <LogoSetup
                            logoUrl={status.company?.LogoUrl}
                            uploading={uploadingLogo}
                            disabled={!status.company?.Name}
                            onFileSelected={onLogoSelected}
                            onFileRejected={(message, reason) => {
                                showNotification({ color: 'yellow.7', message });
                                trackOnboarding(ONBOARDING_EVENTS.logoUploadFailed, { reason });
                            }}
                            skipped={status.skips.logo}
                            onSkip={() => {
                                trackOnboarding(ONBOARDING_EVENTS.logoSkipped);
                                status.skip('logo');
                            }}
                        />
                        <ImportDataSetup
                            integration={status.integration}
                            completedImportCount={status.completedImportCount}
                            onLearnMore={() => setIntegrationInfoOpen(true)}
                            onManageIntegration={() => openInNewTab(ONBOARDING_ROUTES.integration)}
                            onViewImports={() => {
                                trackOnboarding(ONBOARDING_EVENTS.manualImportClicked, { location: 'dashboard' });
                                go(ONBOARDING_ROUTES.importList);
                            }}
                        />
                    </Stack>
                </OnboardingStepCard>

                <OnboardingStepCard
                    index={3}
                    stepId="firstJob"
                    title="Create your first job"
                    description="A quick sample job card so you can see exactly how ServCraft works. This will be easy to edit once created."
                    complete={steps.complete.firstJob}
                    headerAction={steps.complete.firstJob
                        ? <PrimaryButton id="onboarding-add-more-jobs" miw={HEADER_BUTTON_WIDTH} leftSection={<IconCirclePlus size={20} />} onClick={() => go(ONBOARDING_ROUTES.createJob)}>Add more jobs</PrimaryButton>
                        : <PrimaryButton id="onboarding-create-job" miw={HEADER_BUTTON_WIDTH} leftSection={<IconCirclePlus size={20} />} onClick={() => go(ONBOARDING_ROUTES.createJob)}>Create job</PrimaryButton>}
                    highlighted={isActive('firstJob')}
                    onToggle={() => toggle('firstJob')}
                />
            </Stack>

            <BeforeYouStartModal
                opened={beforeYouStartOpen}
                onClose={() => setBeforeYouStartOpen(false)}
                onStartExploring={() => beforeYouStartOutcome('start_exploring')}
                onBookDemo={() => openBookDemo('before_you_start')}
                firstName={firstName}
                manager={accountManager ?? demoManager}
                demoBooked={demoBooked}
                demoWhen={timing.kind === 'upcoming' ? formatDemoRelative(timing.days) : null}
            />
            <BookDemoModal
                opened={bookDemoOpen}
                onClose={closeBookDemo}
                onBooked={onBooked}
                location={bookDemoLocation}
                meetingUrl={HUBSPOT_DEMO_MEETING_URL}
                user={{ firstName: user?.firstName, lastName: user?.lastName, email: user?.email }}
            />
            <InviteEmployeesModal
                open={inviteOpen}
                origin="onboarding"
                defaultRole={ONBOARDING_INVITE_ROLE}
                cancelLabel={invitingMore ? 'Cancel' : undefined}
                onClose={() => setInviteOpen(false)}
                // An invite creates the employee, so re-reading the list completes the step behind the open dialog.
                onSent={() => status.refetchEmployees()}
                onResults={({ sent, failed }) => {
                    if (sent.length > 0) {
                        inviteOutcome('invited');
                        trackOnboarding(ONBOARDING_EVENTS.adminInvited, { invited_count: sent.length, roles: Array.from(new Set(sent.map(e => e.role))) });
                    }
                    if (failed > 0) trackOnboarding(ONBOARDING_EVENTS.adminInviteFailed, { failed_count: failed });
                }}
            />
            <IntegrationInfoModal
                opened={integrationInfoOpen}
                onClose={() => setIntegrationInfoOpen(false)}
                initialPartner={status.integration?.Partner}
                onViewIntegrations={partner => {
                    connectYourDataOutcome('connect_now');
                    trackOnboarding(ONBOARDING_EVENTS.connectClicked, { package: packageSlug(partner), location: 'connect_your_data' });
                    openInNewTab(ONBOARDING_ROUTES.integration);
                }}
                onBookDemo={() => openBookDemo('connect_your_data')}
                demoBooked={demoBooked}
            />
        </Box>
    );
};

export default OnboardingDashboard;
